import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Navigation from "@/components/Navigation";
import OrderTable from "@/components/OrderTable";
import OrderForm from "@/components/OrderForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  fetchOrders,
  fetchClientData,
  fetchCategories,
  fetchVeilLengths,
  fetchColors,
  fetchProductsByCategory,
  linkOrderToClient,
  type ApiResponse,
  type Order as ApiOrder,
  type Category as ApiCategory,
  type VeilLength as ApiVeilLength,
  type Color as ApiColor,
  type Client as ApiClient,
} from "@/lib/api";
import { useProducts } from "@/hooks/useProducts";

// Extended interfaces for component use
interface Order extends Omit<ApiOrder, 'Note'> {
  Id: number;
  orderId: number;  // 6-digit order ID
  phone: string;
  email: string;
  Note: string;
}

interface Category extends ApiCategory {
  Orders?: number;
}

type VeilLength = ApiVeilLength;
type Color = ApiColor;
type Client = ApiClient;

interface OrderItem {
  categoryId: string;
  productId: string;
  veilLength: string;
  customSize?: string;
  color: string;
  quantity: number;
}

interface OrderFormData {
  brideName: string;
  weddingDate: string;
  requiredDate: string;
  deliverTo: "Shop" | "Bride";
  clientAddress: string;
  specialRequest: string;
  items: OrderItem[];
}

type ProcessStatus = "New" | "Contact Client" | "Processing" | "Deliver" | "Done";

// Utility function to validate process status transitions
const isValidProcessTransition = (
  currentStatus: ProcessStatus,
  newStatus: ProcessStatus
): boolean => {
  const statusOrder: ProcessStatus[] = [
    "New",
    "Contact Client",
    "Processing",
    "Deliver",
    "Done",
  ];
  const currentIndex = statusOrder.indexOf(currentStatus);
  const newIndex = statusOrder.indexOf(newStatus);
  return newIndex > currentIndex;
};

interface OrderRequestBody {
  orderId: number;  // 6-digit order ID
  phone: string;
  email: string;
  brideName: string;
  Note: string;
  weddingDate: string;
  requiredDate: string;
  deliverTo: string;
  sendAddress: string;
  process: ProcessStatus;
  name: string;
  orderItem: string;
  Clients: number;
}

// Add date formatting utility function
const formatDateForAPI = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toISOString().split("T")[0];
};

// Add random order ID generator
const generateRandomOrderId = (): number => {
  return Math.floor(Math.random() * 1000000) + 1;
};

const Order: React.FC = () => {
  const [isNewOrderOpen, setIsNewOrderOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<
    Record<number, string>
  >({});
  const [formData, setFormData] = useState<OrderFormData>({
    brideName: "",
    weddingDate: "",
    requiredDate: "",
    deliverTo: "Shop",
    clientAddress: "",
    specialRequest: "",
    items: [],
  });

  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const clientId = localStorage.getItem("clientId");

  // Redirect to login if not logged in
  React.useEffect(() => {
    if (!clientId) {
      navigate("/login");
    }
  }, [clientId, navigate]);

  // Fetch orders with proper query key that includes clientId
  const { data: ordersResponse, isLoading: isLoadingOrders, refetch: refetchOrders } = useQuery<ApiResponse<ApiOrder>>({
    queryKey: ["orders"],
    queryFn: fetchOrders,
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Fetch client data
  const { data: clientData } = useQuery<Client>({
    queryKey: ["client", clientId],
    queryFn: () => fetchClientData(clientId || ""),
    enabled: !!clientId,
  });

  // Fetch categories
  const { data: categoriesResponse } = useQuery<ApiResponse<Category>>({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // Fetch veil lengths
  const { data: veilLengthsResponse } = useQuery<ApiResponse<VeilLength>>({
    queryKey: ["veilLengths"],
    queryFn: fetchVeilLengths,
  });

  // Fetch colors
  const { data: colorsResponse } = useQuery<ApiResponse<Color>>({
    queryKey: ["colors"],
    queryFn: fetchColors,
  });

  // Extract data from responses
  const orders: Order[] = ordersResponse?.list.map(order => {
    console.log('Raw order from API:', order); // Debug log
    return {
      ...order,
      phone: clientData?.phone || "",
      email: clientData?.email || "",
      Note: order.Note,
    };
  }) || [];

  const shopAddress = clientData?.shopAddress || "";
  const phone = clientData?.phone || "";
  const email = clientData?.email || "";
  const name = clientData?.name || "";

  // Invalidate orders query when clientId changes
  useEffect(() => {
    if (clientId) {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    }
  }, [clientId, queryClient]);

  // Debug orders data
  useEffect(() => {
    console.log("Orders data:", {
      loading: isLoadingOrders,
      error: !orders,
      data: orders,
      clientId,
    });
  }, [orders, isLoadingOrders, clientId]);

  const handleSubmitOrder = async (formData: OrderFormData) => {
    try {
      // Check if all required data is loaded
      if (!clientData) {
        throw new Error("Client data is still loading");
      }

      if (!categoriesResponse || !veilLengthsResponse || !colorsResponse) {
        throw new Error("Required data is still loading");
      }

      // Validate required fields
      if (!formData.requiredDate || !formData.weddingDate) {
        toast({
          title: "Error",
          description: "Please fill in all required dates",
          variant: "destructive",
        });
        return;
      }

      // Validate bride name only if delivery is to bride
      if (formData.deliverTo === "Bride" && !formData.brideName) {
        toast({
          title: "Error",
          description: "Please enter the bride's name",
          variant: "destructive",
        });
        return;
      }

      // Validate delivery address for bride delivery
      if (formData.deliverTo === "Bride" && !formData.clientAddress) {
        toast({
          title: "Error",
          description: "Please enter the delivery address",
          variant: "destructive",
        });
        return;
      }

      if (formData.items.length === 0) {
        toast({
          title: "Error",
          description: "Please add at least one item to your order",
          variant: "destructive",
        });
        return;
      }

      // Validate each order item
      for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        if (
          !item.categoryId ||
          !item.productId ||
          !item.veilLength ||
          !item.color ||
          !item.quantity ||
          (item.veilLength === "custom" && !item.customSize)
        ) {
          toast({
            title: "Error",
            description: `Please fill out all fields for order item ${i + 1}${
              item.veilLength === "custom" ? ", including custom size" : ""
            }`,
            variant: "destructive",
          });
          return;
        }
      }

      const orderPromises = formData.items.map(async (item) => {
        try {
          // Get the product, veil length, and color titles
          const category = categoriesResponse?.list?.find(
            (c) => String(c.Id) === item.categoryId
          );
          const products = await fetchProductsByCategory(item.categoryId);
          const product = products?.list?.find(
            (p: { Id: number; Title: string }) =>
              String(p.Id) === item.productId
          );

          // Handle custom veil length differently
          let veilLength;
          if (item.veilLength === "custom") {
            veilLength = { Id: "custom", Title: item.customSize };
          } else {
            veilLength = veilLengthsResponse?.list?.find(
              (v: { Id: number; Title: string }) =>
                String(v.Id) === item.veilLength
            );
          }

          const color = colorsResponse?.list?.find(
            (c: { Id: number; Title: string }) => String(c.Id) === item.color
          );

          if (!category || !product || (!veilLength && item.veilLength !== "custom") || !color) {
            console.error("Missing product details:", {
              categoryId: item.categoryId,
              productId: item.productId,
              veilLengthId: item.veilLength,
              colorId: item.color,
              found: {
                category: category?.Title,
                product: product?.Title,
                veilLength: veilLength?.Title,
                color: color?.Title,
              },
            });
            throw new Error("Failed to get complete product details");
          }

          // Create order item string with proper handling of custom size
          const orderItemString = item.veilLength === "custom"
            ? `${item.quantity} | ${product.Title} | ${item.customSize} | ${color.Title}`
            : `${item.quantity} | ${product.Title} | ${veilLength.Title} | ${color.Title}`;

          const requestBody: OrderRequestBody = {
            orderId: generateRandomOrderId(),
            phone,
            email,
            brideName: formData.brideName,
            Note: formData.specialRequest || "",
            weddingDate: formatDateForAPI(formData.weddingDate),
            requiredDate: formatDateForAPI(formData.requiredDate),
            deliverTo: formData.deliverTo,
            sendAddress:
              formData.deliverTo === "Shop"
                ? shopAddress
                : formData.clientAddress,
            process: "New" as ProcessStatus,
            name,
            orderItem: orderItemString,
            Clients: clientId ? parseInt(clientId) : 0,
          };

          const response = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/tables/${
              import.meta.env.VITE_ORDERS_TABLE
            }/records`,
            {
              method: "POST",
              headers: {
                accept: "application/json",
                "Content-Type": "application/json",
                "xc-token": import.meta.env.VITE_API_TOKEN,
              },
              body: JSON.stringify(requestBody),
            }
          );

          const responseText = await response.text();
          if (!response.ok) {
            let errorData;
            try {
              errorData = JSON.parse(responseText);
            } catch (e) {
              errorData = { msg: responseText };
            }
            throw new Error(
              `Failed to create order: ${
                errorData?.msg || `${response.status} ${response.statusText}`
              }`
            );
          }

          const data = JSON.parse(responseText);
          console.log("Order created successfully:", data);

          // Link the order to the client
          if (clientId) {
            try {
              await linkOrderToClient(parseInt(clientId), data.Id);
              console.log("Order linked to client successfully");
            } catch (linkError) {
              console.error("Error linking order to client:", linkError);
              // Don't throw here, as the order was created successfully
            }
          }

          return data;
        } catch (error) {
          console.error("Error in order promise:", error);
          throw error;
        }
      });

      const results = await Promise.all(orderPromises);
      console.log("All orders created and linked successfully:", results);

      // Reset form after successful submission
      setFormData({
        brideName: "",
        weddingDate: "",
        requiredDate: "",
        deliverTo: "Shop",
        clientAddress: "",
        specialRequest: "",
        items: [],
      });
      setIsNewOrderOpen(false);

      // Show success message
      toast({
        title: "Success",
        description: "Order created successfully",
      });

      // Refresh orders data immediately
      await refetchOrders();
      
      // Also invalidate the query to ensure it's fresh for next time
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (error) {
      console.error("Error in order submission:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to submit order",
        variant: "destructive",
      });
    }
  };

  const handleCategoryChange = (index: number, categoryId: string) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [index]: categoryId,
    }));
  };

  const handleFormDataChange = (formData: OrderFormData) => {
    // Clear bride name when switching to shop delivery
    if (formData.deliverTo === "Shop") {
      formData.brideName = "";
    }
    setFormData(formData);
  };

  const { getProductsForItem, isLoadingProductsForItem } =
    useProducts(selectedCategories);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Orders</h1>
          <Button onClick={() => setIsNewOrderOpen(true)}>New Order</Button>
        </div>

        {isLoadingOrders ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
          </div>
        ) : !orders ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">No orders found</p>
          </div>
        ) : (
          <OrderTable orders={orders} />
        )}
      </div>

      <Dialog open={isNewOrderOpen} onOpenChange={setIsNewOrderOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-4 border-b">
            <DialogTitle>New Order</DialogTitle>
          </DialogHeader>
          <OrderForm
            categories={categoriesResponse?.list.map(category => ({
              ...category,
              Orders: 0, // Default value since it's not provided by the API
            })) || []}
            veilLengths={veilLengthsResponse?.list || []}
            colors={colorsResponse?.list || []}
            onSubmit={handleSubmitOrder}
            onCancel={() => setIsNewOrderOpen(false)}
            onCategoryChange={handleCategoryChange}
            getProductsForItem={getProductsForItem}
            isLoadingProductsForItem={isLoadingProductsForItem}
            formData={formData}
            setFormData={handleFormDataChange}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Order;
